import React, { useState, useEffect, useMemo } from 'react';
import { FaClone, FaDatabase, FaSpinner, FaTimes, FaCheck, FaFilter, FaTimesCircle, FaCog, FaUser } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { apiService } from '../../services/apiService';
import { logger } from '../../utils/logger';
import SelectField from '../../common/Input/SelectField';
import useLookupOptions from '../../hooks/useLookupOptions';

/**
 * @component DuplicaCampiModal
 * @description Componente modale per la selezione e duplicazione di campi da una tabella esistente.
 * Ora include il filtro per modulo e la possibilità di selezionare campi tecnici o applicativi.
 * È disaccoppiato dalla logica di salvataggio finale, che viene delegata al componente
 * padre tramite la callback `onDuplicate`.
 *
 * @param {object} props - Le props del componente.
 * @param {boolean} props.isOpen - Controlla la visibilità del modal.
 * @param {function} props.onClose - Funzione per chiudere il modal.
 * @param {function} props.onDuplicate - Callback che riceve un array di oggetti campo pronti per la creazione.
 * @param {Array<object>} props.tabelleOptions - Opzioni per il selettore delle tabelle (con TAB_Id_Moduli).
 * @param {string} [props.themeColor='emerald'] - Colore del tema per lo styling.
 */
function DuplicaCampiModal({
  isOpen,
  onClose,
  onDuplicate,
  tabelleOptions = [],
  themeColor = 'emerald',
}) {
  // Stato per l'ID della tabella sorgente selezionata
  const [selectedSourceTableId, setSelectedSourceTableId] = useState('');
  // Stato per la lista dei campi caricati dalla tabella sorgente
  const [sourceCampi, setSourceCampi] = useState([]);
  // Stato per gli ID dei campi selezionati per la duplicazione
  const [selectedCampiIds, setSelectedCampiIds] = useState([]);
  // Stato per indicare il caricamento dei campi sorgente
  const [isLoadingSourceCampi, setIsLoadingSourceCampi] = useState(false);
  
  // Stati per il filtro per modulo
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [showModuleFilter, setShowModuleFilter] = useState(false);

  // NUOVO: Stato per il tipo di campi da mostrare
  const [campoFilter, setCampoFilter] = useState('applicativi'); // 'applicativi', 'tecnici', 'tutti'

  // Hook per caricare i moduli disponibili
  const moduliLookupConfig = useMemo(() => ({
    moduliLookup: {
      tableName: 'SKE_Moduli',
      valueField: 'MOD_Id_Moduli',
      labelField: 'MOD_DescrizioneModulo',
      additionalFields: ['MOD_CodiceModulo'], // Per mostrare anche il codice
    },
  }), []);

  const { 
    lookupOptions: moduliLookupOptions, 
    loading: isLoadingModuli 
  } = useLookupOptions(moduliLookupConfig);

  // Filtro delle tabelle per modulo selezionato
  const filteredTabelleOptions = useMemo(() => {
    if (selectedModuleIds.length === 0) {
      return tabelleOptions;
    }
    
    return tabelleOptions.filter(tabella => {
      const moduleId = tabella.original?.TAB_Id_Moduli;
      return moduleId && selectedModuleIds.includes(moduleId);
    });
  }, [tabelleOptions, selectedModuleIds]);

  // NUOVO: Filtro dei campi in base al tipo selezionato
  const filteredSourceCampi = useMemo(() => {
    if (campoFilter === 'tutti') {
      return sourceCampi; // Mostra tutti i campi
    }
    
    return sourceCampi.filter(campo => {
      const sequenza = Number(campo.CAM_Sequenza) || 0;
      
      if (campoFilter === 'tecnici') {
        // Campi tecnici: sequenza 0 o maggiore di 1000
        return sequenza === 0 || sequenza > 1000;
      } else {
        // Campi applicativi: sequenza tra 1 e 1000
        return sequenza > 0 && sequenza <= 1000;
      }
    });
  }, [sourceCampi, campoFilter]);

  // Opzioni moduli per il checkbox group
  const availableModules = useMemo(() => {
    const modules = moduliLookupOptions.moduliLookup || [];
    return modules.map(mod => ({
      value: mod.value,
      label: `${mod.original?.MOD_CodiceModulo || mod.value} - ${mod.label}`,
      id: mod.value,
    }));
  }, [moduliLookupOptions.moduliLookup]);

  // Resetta lo stato interno quando il modal viene chiuso per pulizia
  useEffect(() => {
    if (!isOpen) {
      setSelectedSourceTableId('');
      setSourceCampi([]);
      setSelectedCampiIds([]);
      setSelectedModuleIds([]);
      setShowModuleFilter(false);
      setCampoFilter('applicativi'); // Reset al default
    }
  }, [isOpen]);

  // NUOVO: Aggiorna la selezione dei campi quando cambia il filtro
  useEffect(() => {
    // Rimuovi dalla selezione i campi che non sono più visibili
    setSelectedCampiIds(prev => 
      prev.filter(id => filteredSourceCampi.some(campo => campo.CAM_Id_Campi === id))
    );
  }, [filteredSourceCampi]);

  /**
   * Carica i campi dalla tabella sorgente selezionata.
   * MODIFICATO: Ora carica TUTTI i campi, il filtro viene applicato nel frontend
   * @param {string|number} sourceTableId - L'ID della tabella da cui caricare i campi.
   */
  async function handleLoadSourceCampi(sourceTableId) {
    if (!sourceTableId) {
      setSourceCampi([]);
      setSelectedCampiIds([]);
      return;
    }

    setIsLoadingSourceCampi(true);
    try {
      logger.info(`[DuplicaCampiModal] Caricamento campi da tabella sorgente ID: ${sourceTableId}`);
      
      const apiData = await apiService.get('/crud/SKE_Campi', {
        params: {
          filters: JSON.stringify({ CAM_Id_Tabelle: sourceTableId }),
          limit: 1000,
        }
      });

      if (apiData && apiData.records) {
        // MODIFICATO: Carica TUTTI i campi e li ordina per sequenza
        const tuttiiCampi = apiData.records
          .sort((a, b) => (a.CAM_Sequenza || 0) - (b.CAM_Sequenza || 0));
        
        setSourceCampi(tuttiiCampi);
        
        // Pre-seleziona i campi in base al filtro corrente
        const campiVisibili = tuttiiCampi.filter(campo => {
          const sequenza = Number(campo.CAM_Sequenza) || 0;
          
          if (campoFilter === 'tutti') {
            return true;
          } else if (campoFilter === 'tecnici') {
            return sequenza === 0 || sequenza > 1000;
          } else {
            return sequenza > 0 && sequenza <= 1000;
          }
        });
        
        setSelectedCampiIds(campiVisibili.map(campo => campo.CAM_Id_Campi));
      } else {
        setSourceCampi([]);
        setSelectedCampiIds([]);
      }
    } catch (error) {
      logger.error(`[DuplicaCampiModal] Errore nel caricamento campi sorgente:`, error);
      toast.error(`Errore nel caricamento dei campi: ${error.message || 'Errore sconosciuto'}`);
    } finally {
      setIsLoadingSourceCampi(false);
    }
  }

  /**
   * Prepara i dati dei campi selezionati e li passa al componente padre
   * tramite la callback `onDuplicate` per il salvataggio finale.
   */
  function handleConfirmDuplicate() {
    if (selectedCampiIds.length === 0) {
      toast.warning('Seleziona almeno un campo da duplicare.');
      return;
    }

    // Filtra i campi sorgente in base agli ID selezionati
    const campiToDuplicate = sourceCampi.filter(campo => selectedCampiIds.includes(campo.CAM_Id_Campi));

    // MODIFICATO: Gestisce diversamente i campi tecnici vs applicativi
    const dataForCreation = campiToDuplicate.map(campo => {
      const { CAM_Id_Campi, CAM_IdRif_Campi, ...rest } = campo;
      const sequenzaOriginale = Number(campo.CAM_Sequenza) || 0;
      const isCampoTecnico = sequenzaOriginale === 0 || sequenzaOriginale > 1000;
      
      return {
        ...rest,
        CAM_Incrementale: false, // L'incrementale non deve mai essere duplicato
        // NUOVO: Per i campi tecnici, preserva la sequenza originale
        // Per i campi applicativi, la sequenza verrà calcolata nel componente padre
        ...(isCampoTecnico && { CAM_Sequenza: sequenzaOriginale })
      };
    });

    // Chiama la callback del genitore con i dati pronti
    onDuplicate(dataForCreation);
    onClose(); // Chiude il modal dopo aver passato i dati
  }

  // Gestisce la selezione/deselezione di tutti i campi VISIBILI
  function handleToggleSelectAll(checked) {
    setSelectedCampiIds(checked ? filteredSourceCampi.map(c => c.CAM_Id_Campi) : []);
  }

  // Gestisce la selezione di un singolo campo
  function handleToggleSelectCampo(checked, campoId) {
    setSelectedCampiIds(prev => 
      checked ? [...prev, campoId] : prev.filter(id => id !== campoId)
    );
  }

  // Gestisce la selezione dei moduli
  function handleModuleSelectionChange(moduleId, checked) {
    setSelectedModuleIds(prev => {
      const newSelection = checked 
        ? [...prev, moduleId]
        : prev.filter(id => id !== moduleId);
      
      // Se si cambia il filtro dei moduli e una tabella è già selezionata,
      // verifica se la tabella selezionata è ancora visibile
      if (selectedSourceTableId && newSelection.length > 0) {
        const selectedTable = tabelleOptions.find(t => t.value === selectedSourceTableId);
        const selectedTableModuleId = selectedTable?.original?.TAB_Id_Moduli;
        
        if (selectedTableModuleId && !newSelection.includes(selectedTableModuleId)) {
          // La tabella selezionata non è più visibile, deselezionala
          setSelectedSourceTableId('');
          setSourceCampi([]);
          setSelectedCampiIds([]);
        }
      }
      
      return newSelection;
    });
  }

  // Resetta il filtro moduli
  function handleClearModuleFilter() {
    setSelectedModuleIds([]);
  }

  // NUOVO: Gestisce il cambio del filtro campi
  function handleCampoFilterChange(newFilter) {
    setCampoFilter(newFilter);
    
    // Se c'è una tabella selezionata, aggiorna la pre-selezione
    if (selectedSourceTableId && sourceCampi.length > 0) {
      const campiVisibili = sourceCampi.filter(campo => {
        const sequenza = Number(campo.CAM_Sequenza) || 0;
        
        if (newFilter === 'tutti') {
          return true;
        } else if (newFilter === 'tecnici') {
          return sequenza === 0 || sequenza > 1000;
        } else {
          return sequenza > 0 && sequenza <= 1000;
        }
      });
      
      setSelectedCampiIds(campiVisibili.map(campo => campo.CAM_Id_Campi));
    }
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl mx-auto max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className={`flex justify-between items-center px-6 py-4 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700`}>
          <h3 className={`text-lg font-semibold text-${themeColor}-800 dark:text-${themeColor}-100 flex items-center`}>
            <FaClone className="mr-3" />
            Duplica Campi da Tabella Esistente
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100">
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-grow">
          {/* Sezione filtro moduli */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-md font-medium text-gray-800 dark:text-gray-200">
                Filtro per Modulo
              </h4>
              <button
                onClick={() => setShowModuleFilter(!showModuleFilter)}
                className={`px-3 py-1 text-sm rounded-md transition-all duration-200 flex items-center space-x-2 ${
                  showModuleFilter || selectedModuleIds.length > 0
                    ? `bg-${themeColor}-100 text-${themeColor}-700 hover:bg-${themeColor}-200 dark:bg-${themeColor}-900/30 dark:text-${themeColor}-300`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                }`}
              >
                <FaFilter className="w-3 h-3" />
                <span>{showModuleFilter ? 'Nascondi Filtro' : 'Mostra Filtro'}</span>
                {selectedModuleIds.length > 0 && (
                  <span className={`ml-1 px-2 py-0.5 text-xs rounded-full bg-${themeColor}-200 text-${themeColor}-800 dark:bg-${themeColor}-800 dark:text-${themeColor}-200`}>
                    {selectedModuleIds.length}
                  </span>
                )}
              </button>
            </div>

            {/* Filtro moduli espandibile */}
            {showModuleFilter && (
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/30">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Seleziona i moduli per filtrare le tabelle:
                  </span>
                  {selectedModuleIds.length > 0 && (
                    <button
                      onClick={handleClearModuleFilter}
                      className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center space-x-1"
                    >
                      <FaTimesCircle className="w-3 h-3" />
                      <span>Pulisci</span>
                    </button>
                  )}
                </div>
                
                {isLoadingModuli ? (
                  <div className="flex items-center justify-center py-4">
                    <FaSpinner className="animate-spin mr-2 text-gray-500" />
                    <span className="text-gray-600 dark:text-gray-400">Caricamento moduli...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                    {availableModules.map((module) => (
                      <label key={module.id} className="flex items-center space-x-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600/30 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={selectedModuleIds.includes(module.id)}
                          onChange={(e) => handleModuleSelectionChange(module.id, e.target.checked)}
                          className={`form-checkbox h-4 w-4 text-${themeColor}-600 rounded`}
                        />
                        <span className="text-gray-700 dark:text-gray-200 truncate">{module.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Info sul filtro attivo */}
            {selectedModuleIds.length > 0 && (
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">Filtro attivo:</span> {selectedModuleIds.length} modulo/i selezionato/i
                {filteredTabelleOptions.length < tabelleOptions.length && (
                  <span className="ml-2">({filteredTabelleOptions.length} di {tabelleOptions.length} tabelle visibili)</span>
                )}
              </div>
            )}
          </div>

          {/* Selezione tabella sorgente */}
          <div className="mb-6">
            <SelectField
              id="sourceTable"
              name="sourceTable"
              label="1. Seleziona Tabella Sorgente"
              placeholder="Cerca e seleziona una tabella..."
              value={selectedSourceTableId}
              onChange={(value) => {
                setSelectedSourceTableId(value || '');
                handleLoadSourceCampi(value || '');
              }}
              options={filteredTabelleOptions}
              disabled={isLoadingSourceCampi}
              isLoading={isLoadingSourceCampi}
              isClearable={true}
              className="mb-2"
              labelClassName="block text-md font-medium text-gray-800 dark:text-gray-200 mb-2"
            />
            {filteredTabelleOptions.length === 0 && selectedModuleIds.length > 0 && (
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                Nessuna tabella trovata per i moduli selezionati. Prova ad aggiungere altri moduli al filtro.
              </p>
            )}
          </div>

          {/* NUOVO: Filtro tipo campi */}
          {selectedSourceTableId && (
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">
                2. Tipo di Campi da Mostrare
              </h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleCampoFilterChange('applicativi')}
                  className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                    campoFilter === 'applicativi'
                      ? `bg-${themeColor}-600 text-white hover:bg-${themeColor}-700`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  <FaUser className="w-4 h-4" />
                  <span>Solo Applicativi (1-1000)</span>
                  {campoFilter === 'applicativi' && (
                    <span className="ml-1 bg-white/20 px-2 py-0.5 text-xs rounded-full">
                      {filteredSourceCampi.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => handleCampoFilterChange('tecnici')}
                  className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                    campoFilter === 'tecnici'
                      ? `bg-${themeColor}-600 text-white hover:bg-${themeColor}-700`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  <FaCog className="w-4 h-4" />
                  <span>Solo Tecnici </span>
                  {campoFilter === 'tecnici' && (
                    <span className="ml-1 bg-white/20 px-2 py-0.5 text-xs rounded-full">
                      {filteredSourceCampi.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => handleCampoFilterChange('tutti')}
                  className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                    campoFilter === 'tutti'
                      ? `bg-${themeColor}-600 text-white hover:bg-${themeColor}-700`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  <FaDatabase className="w-4 h-4" />
                  <span>Tutti i Campi</span>
                  {campoFilter === 'tutti' && (
                    <span className="ml-1 bg-white/20 px-2 py-0.5 text-xs rounded-full">
                      {filteredSourceCampi.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Sezione dei campi */}
          {isLoadingSourceCampi && (
            <div className="flex items-center justify-center py-8">
              <FaSpinner className="animate-spin mr-2 text-gray-500" />
              <span className="text-gray-600 dark:text-gray-400">Caricamento campi...</span>
            </div>
          )}

          {filteredSourceCampi.length > 0 && !isLoadingSourceCampi && (
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">
                3. Seleziona i Campi da Duplicare ({filteredSourceCampi.length})
                {sourceCampi.length > filteredSourceCampi.length && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({sourceCampi.length - filteredSourceCampi.length} nascosti dal filtro)
                  </span>
                )}
              </h4>
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                    <tr>
                      <th className="w-10 px-3 py-2 text-left">
                        <input
                          type="checkbox"
                          checked={selectedCampiIds.length === filteredSourceCampi.length && filteredSourceCampi.length > 0}
                          onChange={(e) => handleToggleSelectAll(e.target.checked)}
                          className={`form-checkbox h-4 w-4 text-${themeColor}-600 rounded`}
                        />
                      </th>
                      <th className="px-3 py-2 text-left font-medium">Nome Campo</th>
                      <th className="px-3 py-2 text-left font-medium">Descrizione</th>
                      <th className="px-3 py-2 text-left font-medium">Tipo</th>
                      <th className="px-3 py-2 text-center font-medium">Seq.</th>
                      <th className="px-3 py-2 text-center font-medium">Cat.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSourceCampi.map((campo) => {
                      const sequenza = Number(campo.CAM_Sequenza) || 0;
                      const isTecnico = sequenza === 0 || sequenza > 1000;
                      
                      return (
                        <tr key={campo.CAM_Id_Campi} className="border-t dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedCampiIds.includes(campo.CAM_Id_Campi)}
                              onChange={(e) => handleToggleSelectCampo(e.target.checked, campo.CAM_Id_Campi)}
                              className={`form-checkbox h-4 w-4 text-${themeColor}-600 rounded`}
                            />
                          </td>
                          <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100">{campo.CAM_Nome}</td>
                          <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{campo.CAM_Descrizione}</td>
                          <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{campo.CAM_Id_TipiCampi_text || campo.CAM_Id_TipiCampi}</td>
                          <td className="px-3 py-2 text-center text-gray-900 dark:text-gray-100">{campo.CAM_Sequenza}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${
                              isTecnico 
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                            }`}>
                              {isTecnico ? 'TEC' : 'APP'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Messaggi di stato */}
          {selectedSourceTableId && !isLoadingSourceCampi && filteredSourceCampi.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FaDatabase className="mx-auto mb-2 text-2xl opacity-50" />
              <p className="text-sm">
                Nessun campo {campoFilter === 'tecnici' ? 'tecnico' : campoFilter === 'applicativi' ? 'applicativo' : ''} 
                {' '}trovato nella tabella selezionata.
              </p>
              {campoFilter !== 'tutti' && (
                <button
                  onClick={() => handleCampoFilterChange('tutti')}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                >
                  Mostra tutti i campi
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
          <div className="flex justify-between items-center">
            <div className="text-sm">
              {selectedCampiIds.length > 0 && (
                <span className="flex items-center">
                  <FaCheck className="mr-2 text-green-500" />
                  {selectedCampiIds.length} campo/i selezionato/i
                  {campoFilter !== 'tutti' && (
                    <span className="ml-2 text-gray-500">
                      ({campoFilter === 'tecnici' ? 'tecnici' : 'applicativi'})
                    </span>
                  )}
                </span>
              )}
            </div>
            <div className="space-x-3">
              <button 
                onClick={onClose} 
                className="px-5 py-2 text-sm font-medium border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
              >
                Annulla
              </button>
              <button
                onClick={handleConfirmDuplicate}
                disabled={selectedCampiIds.length === 0}
                className={`px-5 py-2 text-sm font-medium bg-${themeColor}-600 text-white rounded-lg hover:bg-${themeColor}-700 disabled:opacity-50 flex items-center transition-all duration-200`}
              >
                <FaClone className="mr-2" />
                Duplica Campi
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DuplicaCampiModal;